import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  listTenants,
  getTenant,
  createTenant,
  updateTenant,
  activateTenant,
  deactivateTenant,
  destroyTenant,
  pauseProbe,
  resumeProbe,
  cleanTenantData,
  type CreateTenantDTO,
  type UpdateTenantDTO,
} from '../api/tenants.api.ts';
import type { TenantListParams } from '@/shared/types/tenant.ts';

export const tenantKeys = {
  all: ['tenants'] as const,
  list: (params: TenantListParams) => [...tenantKeys.all, 'list', params] as const,
  detail: (id: string) => [...tenantKeys.all, 'detail', id] as const,
};

export function useTenantList(params: TenantListParams, enabled = true) {
  return useQuery({
    queryKey: tenantKeys.list(params),
    queryFn: () => listTenants(params),
    enabled,
  });
}

export function useTenantDetail(id: string | null) {
  return useQuery({
    queryKey: tenantKeys.detail(id!),
    queryFn: () => getTenant(id!),
    enabled: !!id,
  });
}

export function useCreateTenant(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTenantDTO) => createTenant(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Tenant criado com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.error('Slug já em uso');
      } else {
        toast.error('Erro ao criar tenant');
      }
    },
  });
}

export function useUpdateTenant(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTenantDTO }) => updateTenant(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Tenant atualizado com sucesso');
      onSuccess?.();
    },
    onError: () => {
      toast.error('Erro ao atualizar tenant');
    },
  });
}

export function useActivateTenant(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => activateTenant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Tenant ativado com sucesso');
      onSuccess?.();
    },
    onError: () => {
      toast.error('Erro ao ativar tenant');
    },
  });
}

export function useDeactivateTenant(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deactivateTenant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Tenant desativado com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error('Tenant possui incidente aberto');
      } else {
        toast.error('Erro ao desativar tenant');
      }
    },
  });
}

export function useDestroyTenant(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => destroyTenant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Tenant excluído permanentemente');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error('Tenant possui incidente aberto');
      } else {
        toast.error('Erro ao excluir tenant');
      }
    },
  });
}

export function usePauseProbe(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pauseProbe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Probes pausados com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error('Não é possível pausar probes deste tenant');
      } else {
        toast.error('Erro ao pausar probes');
      }
    },
  });
}

export function useResumeProbe(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resumeProbe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Probes retomados com sucesso');
      onSuccess?.();
    },
    onError: () => {
      toast.error('Erro ao retomar probes');
    },
  });
}

export function useCleanTenantData(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, before }: { id: string; before?: string }) => cleanTenantData(id, before),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      toast.success('Dados do tenant limpos com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error('Probes devem estar pausados para limpar dados');
      } else {
        toast.error('Erro ao limpar dados do tenant');
      }
    },
  });
}

function isAxiosError(err: unknown): err is { response?: { status: number } } {
  return typeof err === 'object' && err !== null && 'response' in err;
}
